import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, BellRing, Loader2, Tags } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { when } from './PlatformConsole';
import { RemindAllButton, HistoryButton } from './PlatformRolloutReminders';
import { PlatformRolloutChart } from './PlatformRolloutChart';

const STATE_PILL = { updated: 'active', behind: 'past_due', unknown: 'pending' };
const STATE_LABEL = { updated: 'Updated', behind: 'Needs update', unknown: 'Version unknown' };

const RemindButton = ({ till, onSent }) => {
  const [busy, setBusy] = useState(false);
  const recent = till.reminded_at && Date.now() - new Date(till.reminded_at).getTime() < 864e5;
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/platform/installations/rollout/remind/${till.tenant_id}`);
      const via = [data.email_ok && 'email', data.sms_ok && 'SMS'].filter(Boolean).join(' + ');
      toast.success(`Reminder sent to ${till.branch_name}${via ? ` by ${via}` : ''} and shown in their bell`);
      onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send the reminder'); }
    setBusy(false);
  };
  return (
    <div>
      <button type="button" className="pc-btn" disabled={busy || recent} title={recent ? 'Already reminded in the last 24 hours' : 'Send update reminder'} aria-label="Send update reminder" onClick={send} data-testid={`pc-rollout-remind-${till.tenant_id}`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 inline mr-1 animate-spin" /> : <BellRing className="h-3.5 w-3.5 inline mr-1" />}Remind
      </button>
      {till.reminded_at && <div className="pc-muted" style={{ fontSize: '.7rem', marginTop: '.2rem' }} data-testid={`pc-rollout-reminded-${till.tenant_id}`}>Reminded {when(till.reminded_at)}</div>}
    </div>
  );
};

const RolloutTills = ({ tills, onSent }) => {
  const firstBehind = new Set();
  const shown = new Set();
  const firstRow = new Set();
  const seen = new Set();
  tills.forEach(t => {
    if (t.state === 'behind' && !shown.has(t.tenant_id)) { shown.add(t.tenant_id); firstBehind.add(t.terminal_id); }
    if (!seen.has(t.tenant_id)) { seen.add(t.tenant_id); firstRow.add(t.terminal_id); }
  });
  return (
    <div className="pc-scroll pc-rollout-tills" style={{ maxHeight: '22rem' }}>
      <table className="pc-table">
        <thead><tr><th>Till</th><th>Branch</th><th>Version</th><th>Status</th><th /><th>Old labels</th><th>Last seen</th></tr></thead>
        <tbody>
          {tills.map(t => (
            <tr key={t.terminal_id} data-testid={`pc-rollout-till-${t.terminal_id}`}>
              <td><b style={{ color: '#fff' }}>{t.label}</b><div className="pc-mono">{t.user_name}</div></td>
              <td><Link to={`/platform/installations/${t.tenant_id}`} style={{ color: '#fff' }}>{t.branch_name}</Link>{t.pharmacy_name !== t.branch_name && <div className="pc-mono">{t.pharmacy_name}</div>}</td>
              <td className="pc-mono" data-testid={`pc-rollout-version-${t.terminal_id}`}>{t.app_version || '—'}</td>
              <td><span className={`pc-pill pc-pill-${STATE_PILL[t.state]}`} style={{ whiteSpace: 'nowrap' }} data-testid={`pc-rollout-state-${t.terminal_id}`}>{STATE_LABEL[t.state]}</span></td>
              <td className="pc-num"><div style={{ display: 'flex', gap: '.4rem', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
                {firstBehind.has(t.terminal_id) && <RemindButton till={t} onSent={onSent} />}
                {firstRow.has(t.terminal_id) && <HistoryButton till={t} />}
              </div></td>
              <td data-testid={`pc-rollout-relabel-${t.terminal_id}`}>
                {t.relabel_remaining ? <span style={{ color: '#ffcf72', whiteSpace: 'nowrap' }}><Tags className="h-3.5 w-3.5 inline mr-1" />{t.relabel_remaining} to relabel</span> : <span className="pc-muted" style={{ whiteSpace: 'nowrap' }}>All relabelled</span>}
              </td>
              <td>{when(t.last_seen)}</td>
            </tr>
          ))}
          {tills.length === 0 && <tr><td colSpan={7} className="pc-empty" data-testid="pc-rollout-empty">No tills have connected in the last 30 days.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

export const PlatformRollout = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const load = () => { setError(false); api.get('/platform/installations/rollout').then(r => setData(r.data)).catch(() => setError(true)); };
  useEffect(load, []);
  if (error) return <div className="pc-card pc-muted" style={{ marginBottom: '1.1rem', padding: 16 }} data-testid="pc-rollout-error">Could not load the update rollout. <button type="button" className="pc-btn" onClick={load}>Retry</button></div>;
  if (!data) return null;
  return (
    <>
      <div className="pc-card" style={{ marginBottom: '1.1rem' }} data-testid="pc-rollout">
        <div className="pc-card-h" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '.6rem', flexWrap: 'wrap' }}>
        <h2>Update rollout {data.target_version ? `· ${data.target_version}` : ''}</h2>
        <div style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap' }}>
          <RemindAllButton tills={data.tills} onSent={load} />
          <button type="button" className="pc-btn" onClick={load} data-testid="pc-rollout-refresh"><RefreshCw className="h-3.5 w-3.5 inline mr-1" />Refresh</button>
        </div>
        </div>
        <div style={{ padding: '0 16px 12px' }}>
        <p className="pc-sub" data-testid="pc-rollout-summary">
          <b style={{ color: '#fff' }}>{data.updated}</b> of {data.total} tills seen in the last {data.window_days} days are on {data.target_version || 'the latest version'} ({data.percent}%) · {data.behind} need the update · {data.unknown} unknown
        </p>
        <div style={{ height: 8, borderRadius: 99, background: 'rgba(255,255,255,.08)', overflow: 'hidden', margin: '.5rem 0 .7rem' }}>
          <div data-testid="pc-rollout-bar" style={{ width: `${data.percent}%`, height: '100%', background: '#3fb37f', transition: 'width .6s ease' }} />
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
          {data.by_version.map(v => (
            <span key={v.version} className="pc-mono" style={{ border: '1px solid rgba(255,255,255,.15)', borderRadius: 99, padding: '.15rem .6rem' }} data-testid={`pc-rollout-count-${v.version}`}>
              {v.version}: {v.count}
            </span>
          ))}
        </div>
        </div>
        <RolloutTills tills={data.tills} onSent={load} />
      </div>
      <PlatformRolloutChart targetVersion={data.target_version} />
    </>
  );
};
